import {
  DRAWING_FAN_GRID_OPACITY,
  DRAWING_FAN_GRID_WIDTH_PX,
  DRAWING_LEVEL_LABEL_OFFSET_X_PX,
  DRAWING_LEVEL_LABEL_OFFSET_Y_PX,
} from "./drawingConstants";
import { FIBONACCI_FAN_LEVELS } from "./drawingLevels";
import { rayEnd } from "./drawingRay";
import { mainStroke } from "./drawingShapeParts";

// Level 1 of both axes is the same ray: the diagonal of the grid
const LAST_LEVEL = FIBONACCI_FAN_LEVELS[FIBONACCI_FAN_LEVELS.length - 1];

const rayThrough = (from, through, pane) => {
  const end = rayEnd(from, through, pane);

  return [from.x, from.y, end.x, end.y];
};

const labelsOfSides = (places, first, second, style) => {
  const left = Math.min(first.x, second.x) - DRAWING_LEVEL_LABEL_OFFSET_X_PX;
  const right = Math.max(first.x, second.x) + DRAWING_LEVEL_LABEL_OFFSET_X_PX;
  const top = Math.min(first.y, second.y) - DRAWING_LEVEL_LABEL_OFFSET_Y_PX;
  const bottom = Math.max(first.y, second.y) + DRAWING_LEVEL_LABEL_OFFSET_Y_PX;
  const group = (align, axis, entryOf) => ({
    color: style.levelsColor,
    align,
    axis,
    entries: places.map((place) => ({ ...entryOf(place), text: String(place.level) })),
  });

  return [
    group("right", "y", ({ y }) => ({ x: left, y })),
    group("left", "y", ({ y }) => ({ x: right, y })),
    group("center", "x", ({ x }) => ({ x, y: top })),
    group("center", "x", ({ x }) => ({ x, y: bottom })),
  ];
};

/**
 * Fibonacci Fan (po-chart-visual-spec.md §7.5): the first point is a corner of
 * a grid, the second one the opposite corner. The grid has a horizontal and a
 * vertical line per level, thin and dim. From the first point rays go through
 * the levels of the far sides and on without an end; level 0 gives the
 * horizontal and the vertical ray. The sides of the grid carry the levels. See TOOLS in drawingTools.js.
 *
 * @param {Array<{ x: number, y: number }>} points - CSS px of the pane
 * @param {{ width: number, height: number }} pane
 * @param {object} style - FIBONACCI_FAN_STYLE with colour and width
 * @returns {{ strokes: object[], fills: object[], labels: object[] }}
 */
export const fibonacciFanShape = ([first, second], pane, style) => {
  const places = FIBONACCI_FAN_LEVELS.map((level) => ({
    level,
    x: first.x + (second.x - first.x) * level,
    y: first.y + (second.y - first.y) * level,
  }));
  const raysThroughFarSides = places.flatMap(({ level, x, y }) => {
    const throughSide = rayThrough(first, { x: second.x, y }, pane);

    return level === LAST_LEVEL ? [throughSide] : [throughSide, rayThrough(first, { x, y: second.y }, pane)];
  });
  const gridLines = places.flatMap(({ x, y }) => [
    [first.x, y, second.x, y],
    [x, first.y, x, second.y],
  ]);

  return {
    strokes: [
      { segments: gridLines, color: style.color, width: DRAWING_FAN_GRID_WIDTH_PX, opacity: DRAWING_FAN_GRID_OPACITY },
      mainStroke(style, raysThroughFarSides),
    ],
    fills: [],
    labels: style.isLevelsVisible ? labelsOfSides(places, first, second, style) : [],
  };
};
